import { RFResponse } from "@/interfaces/common";
import {
	RecordQueryParams,
	RecordTransferOuterStatus,
	RecordTransferType,
	TransferRecord,
	TransferRecordDetailRes,
} from "@/interfaces/transferTypes";
import * as http from "@/utils/request";
import dayjs from "dayjs";
import useSWR from "swr";
import useSWRInfinite, { SWRInfiniteKeyLoader } from "swr/infinite";
import type { ScopedMutator } from "swr";

const baseUrl = process.env.NEXT_PUBLIC_BASE_API;

export const fetchTransferRecords = async () => {
	const url = `${baseUrl}/masterAccount/api/masterAccounts/transfers?transferType=${RecordTransferType.deposit}&limit=1&status=${RecordTransferOuterStatus.unfinished}`;

	try {
		const { code, data } = await http.get<RFResponse<TransferRecord[]>>(url);
		if (code !== 200) throw Error("Failed to fetch");
		return data;
	} catch (error) {
		throw error;
	}
};

/**
 * 获取指定年月的第一天和最后一天的时间戳
 * @param year 年份，如 2025
 * @param month 月份，1-12
 * @returns { start: number, end: number }
 */
export function getMonthStartEndTimestamp(yearMonth: string) {
	const [year, month] = yearMonth.split("-").map(Number);
	if (!year || !month || month < 1 || month > 12) {
		return { start: undefined, end: undefined };
	}
	const start = dayjs(`${year}-${month.toString().padStart(2, "0")}-01 00:00:00`).valueOf();
	const endDay = dayjs(`${year}-${month.toString().padStart(2, "0")}-01`)
		.endOf("month")
		.date();
	const end = dayjs(`${year}-${month.toString().padStart(2, "0")}-${endDay} 59:59:59`).valueOf();
	return { start, end };
}

/**
 * 让转账记录列表的缓存失效。
 *
 * 列表用的是 useSWRInfinite，缓存里有两类 key：包一层 `$inf$` 前缀的聚合 key，
 * 以及每一页各自的 URL。两者都含有下面这个片段，一次匹配即可全部命中。
 * 注意详情接口是 `/masterAccounts/transfer/{id}/detail`（单数），不会被误伤。
 *
 * 必须把数据置空而不是只触发 revalidate：调用方（详情页）撤单时列表页已卸载、
 * 没有订阅者，revalidate 不会发生，清掉数据才能让它下次挂载重新拉。
 */
const TRANSFER_LIST_KEY_FRAGMENT = "/masterAccounts/transfers";

export const invalidateTransferList = (globalMutate: ScopedMutator) =>
	globalMutate((key) => typeof key === "string" && key.includes(TRANSFER_LIST_KEY_FRAGMENT), undefined, {
		revalidate: true,
	});

export const useFetchTransferRecord = (params: RecordQueryParams) => {
	const getKey: SWRInfiniteKeyLoader = (pageIndex, previousPageData) => {
		if (previousPageData && !previousPageData.data) return null;

		let url = `${baseUrl}/masterAccount/api/masterAccounts/transfers?limit=10`;
		const { type, startDate, endDate, status, month } = params;
		if (type !== -1) {
			url += `&transferType=${type}`;
		}

		if (status !== -1) {
			url += `&status=${status}`;
		}

		if (month !== "all" && month !== "custom" && month) {
			const { start, end } = getMonthStartEndTimestamp(month);
			url += `&startDate=${start}&endDate=${end}`;
		}

		if (month === "custom" && startDate && endDate) {
			const { start } = getMonthStartEndTimestamp(startDate);
			const { end } = getMonthStartEndTimestamp(endDate);
			url += `&startDate=${start}&endDate=${end}`;
		}

		if (pageIndex === 0) {
			return url;
		}

		return `${url}&cursor=${previousPageData.nextCursor}`;
	};

	const fetcher = async (url: string) => {
		try {
			const { data, _meta } = await http.get<RFResponse<TransferRecord[]>>(url);
			return { data, nextCursor: _meta?.next_cursor };
		} catch (error) {
			return { data: undefined, nextCursor: undefined };
		}
	};

	const { data, mutate, size, setSize, isLoading, isValidating } = useSWRInfinite(getKey, fetcher, {
		initialSize: 1,
		revalidateFirstPage: false,
		revalidateAll: false, // 是否重新拉取所有数据
	});

	const loadMore = async () => {
		await setSize(size + 1);
		return true;
	};

	let assets: TransferRecord[] | undefined = undefined;
	if (data) {
		assets = data.reduce((previous, current) => {
			if (current.data) {
				return previous.concat(...current.data);
			}
			return previous;
		}, [] as TransferRecord[]);
	}

	// 对assets 去重
	// const map = new Map();
	// assets = assets?.filter((item) => {
	// 	return map.has(item.transferId.toString()) ? false : map.set(item.transferId.toString(), true);
	// });

	const hasMore = !!(data && data.length > 0 && data[data.length - 1]?.nextCursor);

	return { data: assets, isLoading, mutate, loadMore, hasMore, isValidating };
};

/**
 * @summary 获取转账详情
 * @param {string} transferId
 * @param {RecordTransferType} transferType
 * @returns {TransferRecordDetailRes} 包含入金&出金详情
 */
export const useTransferDetail = (transferId: string, transferType: RecordTransferType) => {
	const url = `${baseUrl}/masterAccount/api/masterAccounts/transfer/${transferId}/detail?transferType=${transferType}`;
	const { data, error, isLoading, mutate } = useSWR(
		transferId ? url : null,
		async () => await http.get<RFResponse<TransferRecordDetailRes>>(url).then((res) => res.data),
		{ revalidateOnFocus: false },
	);

	return {
		data,
		isLoading,
		error,
		mutate,
	};
};

/**
 * @summary 获取汇率
 */
export const useExchangeRate = (base: string, quote: string) => {
	const url = `${baseUrl}/portfolio/api/currencies/${base}${quote}`;
	const { data, error, isLoading } = useSWR(
		url,
		async () =>
			await http
				.get<
					RFResponse<{
						base: string;
						quote: string;
						rate: number;
						realRate: number;
					}>
				>(url)
				.then((res) => res.data),
		{ revalidateOnFocus: false },
	);

	return {
		data,
		isLoading,
		error,
	};
};
